import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Lock, Star } from 'lucide-react';
import { projectsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { bestProject, byName, groupByOrg, orgOf, type OrgGroup } from '@/lib/project-groups';
import { AnchoredPanel } from './AnchoredPanel';
import { useCanFavorite } from '@/hooks/useCanFavorite';

const SECTION_CAPTION = 'px-3 pb-1 pt-3 text-caption font-bold uppercase tracking-wider text-grey-400';
const ORG_CAPTION = 'px-3 pb-0.5 pt-2 text-caption font-semibold text-grey-500';

/** Sections every project has, so a switch can keep you on the same one. */
const SHARED_SECTION = /^\/projects\/[^/]+\/(discussions|tasks|events|files|forms)(\/|$)/;

/**
 * Where you are: the organization and project dropdowns at the top of the nav.
 *
 * Shows only what there is to choose (Jesse, 2026-10-07): the organization
 * dropdown with 2+ organizations, the project dropdown with 2+ projects, and
 * just the project's name when there's one. Most clients see a single
 * dropdown, or none.
 *
 * Organizations and projects are A–Z (numeric-aware, so "Phase 2" precedes
 * "Phase 10"); search looks across every org. Favorites are the ★ on the app
 * bar (FavoritesPanel), not in here (Jesse, 2026-10-07).
 * Every row has its own star, so starring doesn't mean opening the project.
 */
export function ProjectSwitcher({
  currentProjectId,
  onNavigate,
}: {
  currentProjectId?: string;
  /** Closes the mobile drawer; the panel is portalled outside it. */
  onNavigate?: () => void;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects', 'member'],
    queryFn: () => projectsApi.list(),
  });

  // Archived projects stay out, except the one you're standing in
  const visible = useMemo(
    () => (projects ?? []).filter((p: any) => !p.archivedAt || p.id === currentProjectId),
    [projects, currentProjectId],
  );
  const current = visible.find((p: any) => p.id === currentProjectId);
  const groups = useMemo(() => groupByOrg(visible), [visible]);
  const currentOrg = current ? orgOf(current) : undefined;

  const go = (projectId: string) => {
    // Same section in the new project when every project has it
    const section = pathname.match(SHARED_SECTION)?.[1];
    navigate(`/projects/${projectId}${section ? `/${section}` : ''}`);
    onNavigate?.();
  };

  if (isLoading) {
    return <div className="h-[52px] animate-pulse rounded-lg bg-grey-100" aria-busy />;
  }
  if (visible.length === 0) return null;

  // One project: nothing to choose, but always a way back to it — from
  // Activity or Settings a client otherwise had no route home (Jesse,
  // 2026-10-07)
  if (visible.length === 1) {
    const only = visible[0];
    return (
      <Link
        to={`/projects/${only.id}`}
        onClick={onNavigate}
        className="block rounded-lg px-3 py-1.5 transition-colors hover:bg-grey-100"
      >
        <span className="block truncate text-caption font-semibold uppercase tracking-wider text-grey-400">{orgOf(only).name}</span>
        <span className="block truncate text-sm font-semibold text-secondary">{only.name}</span>
      </Link>
    );
  }

  return (
    <div className="space-y-2">
      {groups.length > 1 && (
        <OrgDropdown
          groups={groups}
          currentOrgId={currentOrg?.id}
          onSelect={(group) => {
            const target = bestProject(group.projects);
            if (target) go(target.id);
          }}
        />
      )}
      <ProjectDropdown
        projects={visible}
        currentProject={current}
        // With an org dropdown, the list below favorites is that org's projects
        scopeOrgId={groups.length > 1 ? currentOrg?.id : undefined}
        onSelect={go}
        onNavigate={onNavigate}
      />
    </div>
  );
}

function TriggerButton({
  caption,
  value,
  placeholder,
  open,
  onClick,
  buttonRef,
  ariaLabel,
}: {
  caption: string;
  value?: string;
  placeholder: string;
  open: boolean;
  onClick: () => void;
  buttonRef: React.RefObject<HTMLButtonElement>;
  ariaLabel: string;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onClick}
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-label={ariaLabel}
      className={cn(
        'flex w-full items-center gap-2 rounded-lg border bg-white px-3 py-1.5 text-left transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20',
        open ? 'border-accent' : 'border-grey-200 hover:border-grey-300',
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-caption font-semibold uppercase tracking-wider text-grey-400">{caption}</span>
        <span className={cn('block truncate text-sm font-semibold', value ? 'text-secondary' : 'text-grey-400')}>
          {value ?? placeholder}
        </span>
      </span>
      <ChevronsUpDown className="h-4 w-4 shrink-0 text-grey-400" aria-hidden />
    </button>
  );
}

function OrgDropdown({
  groups,
  currentOrgId,
  onSelect,
}: {
  groups: OrgGroup<any>[];
  currentOrgId?: string;
  onSelect: (group: OrgGroup<any>) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLButtonElement>(null);
  const current = groups.find((g) => g.orgId === currentOrgId);
  const shown = query.trim()
    ? groups.filter((g) => g.orgName.toLowerCase().includes(query.trim().toLowerCase()))
    : groups;

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  return (
    <>
      <TriggerButton
        buttonRef={ref}
        caption="Organization"
        value={current?.orgName}
        placeholder="Select an organization"
        open={open}
        onClick={() => setOpen((v) => !v)}
        ariaLabel={`Organization: ${current?.orgName ?? 'none selected'}. Switch organization`}
      />
      <AnchoredPanel anchorRef={ref} open={open} onClose={close}>
        {groups.length > 8 && <SearchBox value={query} onChange={setQuery} placeholder="Search organizations…" />}
        <ul aria-label="Organizations" className="min-h-0 flex-1 overflow-y-auto py-1">
          {shown.length === 0 && <li className="px-3 py-2 text-sm text-grey-500">No matches.</li>}
          {shown.map((g) => {
            const isCurrent = g.orgId === currentOrgId;
            return (
              <li key={g.orgId}>
                <button
                  type="button"
                  aria-current={isCurrent ? 'true' : undefined}
                  onClick={() => {
                    close();
                    if (!isCurrent) onSelect(g);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-grey-50',
                    isCurrent ? 'font-semibold text-secondary' : 'text-secondary-600',
                  )}
                >
                  <span className="min-w-0 flex-1 truncate">{g.orgName}</span>
                  <span className="num shrink-0 text-caption text-grey-400">{g.projects.length}</span>
                  <Check className={cn('h-4 w-4 shrink-0 text-accent', !isCurrent && 'invisible')} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      </AnchoredPanel>
    </>
  );
}

function ProjectDropdown({
  projects,
  currentProject,
  scopeOrgId,
  onSelect,
  onNavigate,
}: {
  projects: any[];
  currentProject?: any;
  scopeOrgId?: string;
  onSelect: (projectId: string) => void;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLButtonElement>(null);
  const toggleFavorite = useToggleFavorite();
  const canFavorite = useCanFavorite();

  const q = query.trim().toLowerCase();
  // The current org's projects, or everything grouped by org when there's no
  // org dropdown to narrow by. No favorites here: they're the ★ on the app bar
  // (Jesse, 2026-10-07). The row stars stay for agency people, so they can
  // favorite from the list; clients get none (useCanFavorite).
  const inOrg = scopeOrgId
    ? projects.filter((p) => orgOf(p).id === scopeOrgId).sort((a, b) => byName(a.name, b.name))
    : [];
  const scopedOrgName = scopeOrgId ? orgOf(inOrg[0] ?? currentProject).name : undefined;
  // Searching looks across every org
  const matches = q
    ? groupByOrg(projects.filter((p) => p.name.toLowerCase().includes(q) || orgOf(p).name.toLowerCase().includes(q)))
    : [];

  const openPanel = () => setOpen(true);
  const close = () => {
    setOpen(false);
    setQuery('');
  };
  const select = (id: string) => {
    close();
    if (id !== currentProject?.id) onSelect(id);
  };

  const row = (p: any) => (
    <ProjectRow
      key={p.id}
      project={p}
      isCurrent={p.id === currentProject?.id}
      onSelect={select}
      onToggleFavorite={canFavorite ? () => toggleFavorite.mutate(p) : undefined}
    />
  );

  return (
    <>
      <TriggerButton
        buttonRef={ref}
        caption="Project"
        value={currentProject?.name}
        placeholder="Select a project"
        open={open}
        onClick={() => (open ? close() : openPanel())}
        ariaLabel={`Project: ${currentProject?.name ?? 'none selected'}. Switch project`}
      />
      <AnchoredPanel anchorRef={ref} open={open} onClose={close} width={304}>
        {projects.length > 5 && <SearchBox value={query} onChange={setQuery} placeholder="Search all projects…" />}
        <div aria-label="Projects" className="min-h-0 flex-1 overflow-y-auto pb-1">
          {q ? (
            matches.length === 0 ? (
              <p className="px-3 py-3 text-sm text-grey-500">No projects match “{query.trim()}”.</p>
            ) : (
              <GroupedRows groups={matches} row={row} />
            )
          ) : scopeOrgId ? (
            <>
              <p className={SECTION_CAPTION}>{scopedOrgName}</p>
              <ul>{inOrg.map(row)}</ul>
            </>
          ) : (
            <GroupedRows groups={groupByOrg(projects)} row={row} />
          )}
        </div>
        <div className="border-t border-grey-100 px-3 py-2">
          <Link
            to="/projects"
            onClick={() => {
              close();
              onNavigate?.();
            }}
            className="text-sm font-medium text-accent hover:text-accent-700"
          >
            All projects →
          </Link>
        </div>
      </AnchoredPanel>
    </>
  );
}

function GroupedRows({ groups, row }: { groups: OrgGroup<any>[]; row: (p: any) => JSX.Element }) {
  return (
    <>
      {groups.map((g) => (
        <div key={g.orgId}>
          <p className={ORG_CAPTION}>{g.orgName}</p>
          <ul>{g.projects.map(row)}</ul>
        </div>
      ))}
    </>
  );
}

function ProjectRow({
  project,
  isCurrent,
  onSelect,
  onToggleFavorite,
}: {
  project: any;
  isCurrent: boolean;
  onSelect: (id: string) => void;
  /** Absent for non-agency people: no star (useCanFavorite). */
  onToggleFavorite?: () => void;
}) {
  const fav = !!project.isFavorited;
  return (
    <li className="group flex items-center pr-1.5">
      <button
        type="button"
        aria-current={isCurrent ? 'page' : undefined}
        onClick={() => onSelect(project.id)}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-2 py-1.5 pl-5 pr-2 text-left text-sm transition-colors hover:bg-grey-50',
          isCurrent ? 'font-semibold text-secondary' : 'text-secondary-600',
        )}
      >
        <span className="min-w-0 flex-1 truncate">{project.name}</span>
        {project.isPrivate && <Lock className="h-3 w-3 shrink-0 text-grey-400" aria-label="Private" />}
        <Check className={cn('h-4 w-4 shrink-0 text-accent', !isCurrent && 'invisible')} aria-hidden />
      </button>
      {onToggleFavorite && (
        <button
          type="button"
          onClick={onToggleFavorite}
          aria-pressed={fav}
          aria-label={fav ? `Remove ${project.name} from favorites` : `Add ${project.name} to favorites`}
          className={cn(
            'rounded p-1 transition-colors hover:bg-grey-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/20',
            fav ? 'text-warning' : 'text-grey-300 hover:text-warning',
          )}
        >
          <Star className={cn('h-3.5 w-3.5', fav && 'fill-warning')} aria-hidden />
        </button>
      )}
    </li>
  );
}

function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="border-b border-grey-100 p-2">
      <input
        autoFocus
        type="search"
        aria-label={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-md border border-grey-200 px-2.5 py-1.5 text-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
      />
    </div>
  );
}

/** Star or unstar from anywhere: instant in every list, rolled back on failure. */
export function useToggleFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (project: any) =>
      project.isFavorited ? projectsApi.unfavorite(project.id) : projectsApi.favorite(project.id),
    onMutate: async (project: any) => {
      await queryClient.cancelQueries({ queryKey: ['projects', 'member'] });
      const previous = queryClient.getQueryData<any[]>(['projects', 'member']);
      queryClient.setQueryData<any[]>(['projects', 'member'], (list) =>
        list?.map((p) => (p.id === project.id ? { ...p, isFavorited: !project.isFavorited } : p)),
      );
      return { previous };
    },
    onError: (_err, _project, context) => {
      if (context?.previous) queryClient.setQueryData(['projects', 'member'], context.previous);
    },
    onSettled: (_data, _err, project: any) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['project', project.id] });
    },
  });
}
